import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Save, Eye, Upload } from 'lucide-react';
import {
  ProjectDefinitionSection,
  ChallengeAndRoleSection,
  RecognitionSection,
  CapabilitiesSection,
  CreditsSection,
  SimilarProjectsSection,
  BehindTheScenesSection
} from '@/components/ProjectEditorSections';

export default function AdminProjectEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [project, setProject] = useState({
    title: '',
    slug: '',
    category_id: '',
    year: new Date().getFullYear().toString(),
    location: 'Mexico',
    
    // Media
    poster: '',
    thumbnail: '',
    hero_image: '',
    header_image_source: 'poster',
    video_url: '',
    video_platform: 'vimeo',
    
    // Structured Content Fields
    project_definition: '',
    client_funding: '',
    agency: '',
    challenge: '',
    our_role: '',
    approach: '',
    result: '',
    
    // Recognition (list of awards)
    recognition: [],
    
    // Capabilities
    capabilities: [],
    
    // Credits
    credits: [],
    
    // Similar Projects
    similar_project_ids: [],
    
    // Legacy fields for backward compatibility
    logline: '',
    description: '',
    role: '',
    highlights: [],
    
    // Meta
    featured: false,
    show_on_homepage: false,
    image_display_preference: 'thumbnail',
    status: 'draft',
    order: 0,
    behind_the_scenes_gallery: [],
  });

  useEffect(() => {
    const isAuth = sessionStorage.getItem('admin_auth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    loadData();
  }, [id, navigate]);

  const loadData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const categoriesRes = await fetch(`${backendUrl}/api/admin/project-categories`);
      const capabilitiesRes = await fetch(`${backendUrl}/api/admin/capabilities`);
      const projectsRes = await fetch(`${backendUrl}/api/admin/projects`);
      
      const cats = await categoriesRes.json();
      const caps = await capabilitiesRes.json();
      const projs = await projectsRes.json();
      
      setCategories(cats);
      setCapabilities(caps);
      setAllProjects(projs);

      if (id) {
        // Load existing project
        const existingProject = projs.find(p => p.id === id);
        if (existingProject) {
          setProject(existingProject);
        }
      }
    } catch (err) {
      toast.error('Failed to load data');
    }
  };

  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const extractVideoId = (url, platform) => {
    if (!url) return '';
    
    if (platform === 'vimeo') {
      const match = url.match(/vimeo\.com\/(\d+)/);
      return match ? match[1] : url;
    } else if (platform === 'youtube') {
      const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\?]+)/);
      return match ? match[1] : url;
    }
    return url;
  };

  const handleImageUpload = async (file, fieldName) => {
    if (!file) return null;
    
    // Determine image type from field name
    let imageType = 'default';
    if (fieldName === 'poster') imageType = 'poster';
    else if (fieldName === 'thumbnail') imageType = 'thumbnail';
    else if (fieldName === 'hero_image') imageType = 'hero_image';
    else if (fieldName.startsWith('recognition_')) imageType = 'recognition';
    else if (fieldName === 'behind_the_scenes') imageType = 'behind_the_scenes';
    
    setUploadingImage(fieldName);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', imageType);
      
      const response = await fetch(`${backendUrl}/api/admin/upload-image`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Handle validation errors
        if (data.detail && typeof data.detail === 'object' && data.detail.error === 'Image too small') {
          toast.error(
            `Image Too Small`,
            {
              description: `Minimum size: ${data.detail.min_width}×${data.detail.min_height}px. Your image: ${data.detail.current_width}×${data.detail.current_height}px`,
              duration: 6000,
            }
          );
        } else {
          toast.error(data.detail || 'Failed to upload image');
        }
        return null;
      }
      
      if (data.success) {
        // R2 returns complete URLs starting with https://, don't prepend backend URL
        const fullUrl = data.url.startsWith('http') ? data.url : `${backendUrl}${data.url}`;
        
        // Handle behind_the_scenes uploads - add to gallery array
        if (fieldName === 'behind_the_scenes') {
          setProject({ 
            ...project, 
            behind_the_scenes_gallery: [...(project.behind_the_scenes_gallery || []), fullUrl]
          });
        }
        // Only update project state if it's not a recognition image
        // Recognition images are handled by the component's updateRecognition callback
        else if (!fieldName.startsWith('recognition_') && !fieldName.startsWith('gallery_')) {
          setProject({ ...project, [fieldName]: fullUrl });
        }
        
        // Show success with info about resizing
        if (data.dimensions.was_resized) {
          toast.success(
            'Image uploaded and optimized',
            {
              description: `Resized from ${data.dimensions.original_width}×${data.dimensions.original_height}px to ${data.dimensions.width}×${data.dimensions.height}px`,
              duration: 4000,
            }
          );
        } else {
          toast.success('Image uploaded successfully');
        }
        return fullUrl;
      }
      return null;
    } catch (err) {
      toast.error('Failed to upload image');
      return null;
    } finally {
      setUploadingImage(null);
    }
  };

  const handleSave = async (publishNow = false) => {
    if (!project.title || !project.slug || !project.category_id) {
      toast.error('Please fill in title, slug, and category');
      return;
    }

    setLoading(true);
    try {
      const projectData = {
        ...project,
        status: publishNow ? 'published' : 'draft',
      };

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const url = id 
        ? `${backendUrl}/api/admin/projects/${id}`
        : `${backendUrl}/api/admin/projects`;
      
      const method = id ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectData)
      });

      if (!response.ok) throw new Error('Failed to save');

      toast.success(publishNow ? 'Project published!' : 'Project saved as draft');
      navigate('/admin/projects');
    } catch (err) {
      toast.error('Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  const handleUnpublish = async () => {
    if (!window.confirm('Unpublish this project? It will no longer be visible to the public.')) return;
    
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/projects/${id}/unpublish`, {
        method: 'POST'
      });

      if (!response.ok) throw new Error('Failed to unpublish');

      toast.success('Project unpublished');
      setProject({ ...project, status: 'draft' });
    } catch (err) {
      toast.error('Failed to unpublish project');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    if (!project.slug) {
      toast.error('Please save the project first to preview');
      return;
    }
    window.open(`/work/${project.slug}?preview=true`, '_blank');
  };

  const getHeaderImagePreview = () => {
    if (project.header_image_source === 'poster') return project.poster;
    if (project.header_image_source === 'thumbnail') return project.thumbnail;
    if (project.header_image_source === 'custom') return project.hero_image;
    return project.poster || project.thumbnail || project.hero_image;
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--bk-bg))] p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/projects')}>
              <ArrowLeft size={16} />
            </Button>
            <h1 className="font-display text-3xl text-[hsl(var(--bk-fg))]">
              {id ? 'Edit Project' : 'New Project'}
            </h1>
          </div>
          <div className="flex gap-3">
            {project.status === 'published' && id && (
              <Button variant="outline" onClick={handleUnpublish} disabled={loading} className="border-yellow-600 text-yellow-600 hover:bg-yellow-600/10">
                Unpublish
              </Button>
            )}
            <Button variant="outline" onClick={handlePreview} disabled={loading || !project.slug}>
              <Eye size={16} className="mr-2" /> Preview
            </Button>
            <Button variant="outline" onClick={() => handleSave(false)} disabled={loading}>
              <Save size={16} className="mr-2" /> Save Draft
            </Button>
            <Button onClick={() => handleSave(true)} disabled={loading} className="bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))]">
              Publish
            </Button>
          </div>
        </div>

        <Tabs defaultValue="basic" className="space-y-6">
          <TabsList className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))]">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="media">Media</TabsTrigger>
            <TabsTrigger value="content">Content</TabsTrigger>
          </TabsList>

          {/* BASIC INFO TAB */}
          <TabsContent value="basic">
            <div className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-8 space-y-6">
              <h2 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-6">Basic Information</h2>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Title *</Label>
                  <Input
                    value={project.title}
                    onChange={(e) => {
                      const title = e.target.value;
                      setProject({ 
                        ...project, 
                        title,
                        slug: generateSlug(title)
                      });
                    }}
                    placeholder="San Esteban: Freedom, Mysticism and Nopales"
                    className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                  />
                </div>

                <div>
                  <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Slug *</Label>
                  <Input
                    value={project.slug}
                    onChange={(e) => setProject({ ...project, slug: e.target.value })}
                    placeholder="san-esteban"
                    className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-6">
                <div>
                  <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Category *</Label>
                  <Select value={project.category_id} onValueChange={(v) => setProject({ ...project, category_id: v })}>
                    <SelectTrigger className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] z-[110]">
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Year</Label>
                  <Input
                    value={project.year}
                    onChange={(e) => setProject({ ...project, year: e.target.value })}
                    placeholder="2025"
                    className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                  />
                </div>

                <div>
                  <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Location</Label>
                  <Input
                    value={project.location}
                    onChange={(e) => setProject({ ...project, location: e.target.value })}
                    placeholder="Zapopan, Mexico"
                    className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                  />
                </div>
              </div>

              <div>
                <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Logline (Legacy)</Label>
                <Textarea
                  value={project.logline}
                  onChange={(e) => setProject({ ...project, logline: e.target.value })}
                  rows={2}
                  placeholder="A meditation on resilience, identity, and the quiet revolution..."
                  className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                />
              </div>

              <div>
                <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Description (Legacy)</Label>
                <Textarea
                  value={project.description}
                  onChange={(e) => setProject({ ...project, description: e.target.value })}
                  rows={4}
                  placeholder="At the foot of the mountains of Zapopan lies San Esteban..."
                  className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <Switch checked={project.featured} onCheckedChange={(v) => setProject({ ...project, featured: v })} />
                  <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Featured Project</Label>
                </div>
                
                <div className="flex items-center gap-4">
                  <Switch checked={project.show_on_homepage} onCheckedChange={(v) => setProject({ ...project, show_on_homepage: v })} />
                  <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Show on Homepage</Label>
                </div>

                <div className="pt-2">
                  <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2 block">Grid Display Image</Label>
                  <Select 
                    value={project.image_display_preference || 'thumbnail'} 
                    onValueChange={(v) => setProject({ ...project, image_display_preference: v })}
                  >
                    <SelectTrigger className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">
                      <SelectValue placeholder="Select image format" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="thumbnail">Thumbnail (Horizontal 16:9)</SelectItem>
                      <SelectItem value="poster">Poster (Vertical 2:3)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[hsl(var(--bk-fg-muted))] mt-1">
                    Choose which image shows on Homepage & Work grids. Falls back to the other if selected isn't available.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* MEDIA TAB */}
          <TabsContent value="media">
            <div className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-8 space-y-6">
              <h2 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-6">Media Assets</h2>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Poster Image</Label>
                  {project.image_display_preference === 'poster' && !project.poster && (
                    <span className="text-xs font-mono-meta text-amber-500 bg-amber-500/10 px-2 py-1 rounded border border-amber-500/30">
                      ⚠️ PREFERRED FORMAT MISSING
                    </span>
                  )}
                  {project.image_display_preference === 'poster' && project.poster && (
                    <span className="text-xs font-mono-meta text-green-500 bg-green-500/10 px-2 py-1 rounded border border-green-500/30">
                      ✓ Active in grids
                    </span>
                  )}
                </div>
                <p className="text-xs text-[hsl(var(--bk-fg-muted))] mb-2">Min: 1200×1600px, Max: 2400×3200px</p>
                <div className="flex gap-2 items-center">
                  <label className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'poster')}
                      disabled={uploadingImage === 'poster'}
                      className="hidden"
                      id="poster-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                      disabled={uploadingImage === 'poster'}
                      onClick={() => document.getElementById('poster-upload').click()}
                    >
                      <Upload size={16} className="mr-2" />
                      {uploadingImage === 'poster' ? 'Uploading...' : 'Choose Image'}
                    </Button>
                  </label>
                </div>
                {project.poster && (
                  <div className="mt-2">
                    <img src={project.poster} alt="Poster preview" className="w-48 h-64 object-cover rounded" />
                    <Input
                      value={project.poster}
                      onChange={(e) => setProject({ ...project, poster: e.target.value })}
                      placeholder="Or paste URL"
                      className="mt-2 bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] text-xs"
                    />
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Thumbnail Image</Label>
                  {project.image_display_preference === 'thumbnail' && !project.thumbnail && (
                    <span className="text-xs font-mono-meta text-amber-500 bg-amber-500/10 px-2 py-1 rounded border border-amber-500/30">
                      ⚠️ PREFERRED FORMAT MISSING
                    </span>
                  )}
                  {project.image_display_preference === 'thumbnail' && project.thumbnail && (
                    <span className="text-xs font-mono-meta text-green-500 bg-green-500/10 px-2 py-1 rounded border border-green-500/30">
                      ✓ Active in grids
                    </span>
                  )}
                </div>
                <p className="text-xs text-[hsl(var(--bk-fg-muted))] mb-2">Min: 1200×800px, Max: 2400×1600px</p>
                <div className="flex gap-2 items-center">
                  <label className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'thumbnail')}
                      disabled={uploadingImage === 'thumbnail'}
                      className="hidden"
                      id="thumbnail-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                      disabled={uploadingImage === 'thumbnail'}
                      onClick={() => document.getElementById('thumbnail-upload').click()}
                    >
                      <Upload size={16} className="mr-2" />
                      {uploadingImage === 'thumbnail' ? 'Uploading...' : 'Choose Image'}
                    </Button>
                  </label>
                </div>
                {project.thumbnail && (
                  <div className="mt-2">
                    <img src={project.thumbnail} alt="Thumbnail preview" className="w-64 h-40 object-cover rounded" />
                    <Input
                      value={project.thumbnail}
                      onChange={(e) => setProject({ ...project, thumbnail: e.target.value })}
                      placeholder="Or paste URL"
                      className="mt-2 bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] text-xs"
                    />
                  </div>
                )}
              </div>

              <div>
                <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Header Image Source</Label>
                <Select value={project.header_image_source} onValueChange={(v) => setProject({ ...project, header_image_source: v })}>
                  <SelectTrigger className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] z-[110]">
                    <SelectItem value="poster">Use Poster</SelectItem>
                    <SelectItem value="thumbnail">Use Thumbnail</SelectItem>
                    <SelectItem value="custom">Custom Image</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-[hsl(var(--bk-fg-muted))] mt-1">This image will appear in the page header</p>
              </div>

              {project.header_image_source === 'custom' && (
                <div>
                  <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Custom Header Image</Label>
                  <p className="text-xs text-[hsl(var(--bk-fg-muted))] mb-2">Min: 1920×1080px, Max: 3840×2160px</p>
                  <label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'hero_image')}
                      disabled={uploadingImage === 'hero_image'}
                      className="hidden"
                      id="hero-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                      disabled={uploadingImage === 'hero_image'}
                      onClick={() => document.getElementById('hero-upload').click()}
                    >
                      <Upload size={16} className="mr-2" />
                      {uploadingImage === 'hero_image' ? 'Uploading...' : 'Choose Image'}
                    </Button>
                  </label>
                  {project.hero_image && (
                    <Input
                      value={project.hero_image}
                      onChange={(e) => setProject({ ...project, hero_image: e.target.value })}
                      placeholder="Or paste URL"
                      className="mt-2 bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] text-xs"
                    />
                  )}
                </div>
              )}

              {getHeaderImagePreview() && (
                <div className="p-4 bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))] rounded">
                  <p className="text-xs font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Header Preview:</p>
                  <img src={getHeaderImagePreview()} alt="Header preview" className="w-full max-w-md rounded" />
                </div>
              )}

              <div className="border-t border-[hsl(var(--bk-line))] pt-6">
                <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-4">Video</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Platform</Label>
                    <Select value={project.video_platform} onValueChange={(v) => setProject({ ...project, video_platform: v })}>
                      <SelectTrigger className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] z-[110]">
                        <SelectItem value="vimeo">Vimeo</SelectItem>
                        <SelectItem value="youtube">YouTube</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Video URL or ID</Label>
                    <Input
                      value={project.video_url}
                      onChange={(e) => setProject({ ...project, video_url: e.target.value })}
                      placeholder={project.video_platform === 'vimeo' ? '1119918072 or full URL' : 'dQw4w9WgXcQ or full URL'}
                      className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                    />
                  </div>
                </div>

                {project.video_url && (
                  <div className="mt-4 aspect-video rounded-lg overflow-hidden bg-black">
                    {project.video_platform === 'vimeo' ? (
                      <iframe
                        src={`https://player.vimeo.com/video/${extractVideoId(project.video_url, 'vimeo')}?h=0&title=0&byline=0&portrait=0`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="autoplay; fullscreen"
                        title="Video preview"
                      />
                    ) : (
                      <iframe
                        src={`https://www.youtube.com/embed/${extractVideoId(project.video_url, 'youtube')}`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        title="Video preview"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* CONTENT TAB */}
          <TabsContent value="content">
            <div className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-8 space-y-8">
              <h2 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-6">Project Content</h2>
              
              {/* PROJECT DEFINITION, CLIENT, AGENCY */}
              <ProjectDefinitionSection project={project} setProject={setProject} />
              
              <div className="border-t border-[hsl(var(--bk-line))] pt-8" />
              
              {/* PROJECT DETAILS / HIGHLIGHTS */}
              <div>
                <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-4">Project Details</h3>
                <p className="text-xs text-[hsl(var(--bk-fg-muted))] mb-4">
                  Add key details like runtime, format, grants, or technical specs. Each item appears as a bullet point in the sidebar.
                </p>
                
                {/* Highlights List */}
                <div className="space-y-3">
                  {(project.highlights || []).map((highlight, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={highlight}
                        onChange={(e) => {
                          const newHighlights = [...project.highlights];
                          newHighlights[index] = e.target.value;
                          setProject({ ...project, highlights: newHighlights });
                        }}
                        placeholder="Documentary — 27 min"
                        className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newHighlights = project.highlights.filter((_, i) => i !== index);
                          setProject({ ...project, highlights: newHighlights });
                        }}
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  
                  {/* Add Highlight Button */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const newHighlights = [...(project.highlights || []), ''];
                      setProject({ ...project, highlights: newHighlights });
                    }}
                    className="w-full border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] hover:bg-[hsl(var(--bk-bg-elev))]"
                  >
                    + Add Detail
                  </Button>
                </div>
                
                <div className="mt-4 p-3 bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))] rounded text-xs text-[hsl(var(--bk-fg-muted))]">
                  <strong>Examples:</strong>
                  <ul className="mt-2 space-y-1 ml-4 list-disc">
                    <li>Documentary — 27 min</li>
                    <li>Shot on Digital 4K — 2:39 Aspect Ratio</li>
                    <li>IMCINE Production Grant</li>
                    <li>Feature Film — 94 minutes</li>
                  </ul>
                </div>
              </div>
              
              <div className="border-t border-[hsl(var(--bk-line))] pt-8" />
              
              {/* CHALLENGE, ROLE, APPROACH, RESULT */}
              <ChallengeAndRoleSection project={project} setProject={setProject} />
              
              <div className="border-t border-[hsl(var(--bk-line))] pt-8" />
              
              {/* RECOGNITION */}
              <RecognitionSection 
                project={project} 
                setProject={setProject}
                uploadingImage={uploadingImage}
                handleImageUpload={handleImageUpload}
              />
              
              <div className="border-t border-[hsl(var(--bk-line))] pt-8" />
              
              {/* CAPABILITIES */}
              <CapabilitiesSection 
                project={project} 
                setProject={setProject}
                capabilities={capabilities}
              />
              
              <div className="border-t border-[hsl(var(--bk-line))] pt-8" />
              
              {/* CREDITS */}
              <CreditsSection project={project} setProject={setProject} />
              
              <div className="border-t border-[hsl(var(--bk-line))] pt-8" />
              
              {/* BEHIND THE SCENES GALLERY */}
              <BehindTheScenesSection
                project={project}
                setProject={setProject}
                uploadingImage={uploadingImage}
                handleImageUpload={handleImageUpload}
              />
              
              <div className="border-t border-[hsl(var(--bk-line))] pt-8" />
              
              {/* SIMILAR PROJECTS */}
              <SimilarProjectsSection 
                project={project} 
                setProject={setProject}
                allProjects={allProjects}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
